import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '../context/AuthContext';
import { signInSchema, SignInFormData } from '../utils/validationSchemas';
import supabase from '../services/supabaseClient';
import '../styles/auth.css';

export default function SignInAuth() {
  const navigate = useNavigate();
  const { signIn, signInWithOAuth, checkWhitelistStatus, user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInFormData>({
    resolver: zodResolver(signInSchema),
  });

  const onSubmit = async (data: SignInFormData) => {
    try {
      setError(null);
      setLoading(true);
      await signIn(data.email, data.password);
      
      // Wait a moment for auth state to update, then check whitelist
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Get the current user from auth state
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const whitelisted = await checkWhitelistStatus(session.user.id);
        if (whitelisted) {
          navigate('/dashboard');
        } else {
          navigate('/pending-approval');
        }
      } else {
        navigate('/dashboard'); // Fallback if we can't get user
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred during sign in');
    } finally {
      setLoading(false);
    }
  };

  const handleOAuth = async (provider: 'google' | 'github' | 'discord' | 'microsoft') => {
    try {
      setError(null);
      await signInWithOAuth(provider);
    } catch (err: any) {
      setError(err.message || `An error occurred signing in with ${provider}`);
    }
  };

  return (
    <section className="auth-section">
      <div className="container">
        <div className="auth-content">
          <div className="auth-header">
            <h1 className="auth-title text-gradient">
              Welcome back
            </h1>
          </div>

          {error && (
            <div style={{
              padding: '12px 16px',
              marginBottom: '16px',
              backgroundColor: '#fee',
              border: '1px solid #fcc',
              borderRadius: '6px',
              color: '#c33',
              fontSize: '14px',
            }}>
              {error}
            </div>
          )}

          <form className="auth-form" onSubmit={handleSubmit(onSubmit)}>
            <div className="auth-form-fields">
              <div>
                <label htmlFor="email" className="auth-label">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  className="form-input"
                  placeholder="Your email"
                  {...register('email')}
                />
                {errors.email && (
                  <span style={{ color: '#c33', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    {errors.email.message}
                  </span>
                )}
              </div>

              <div>
                <div className="auth-label-row">
                  <label htmlFor="password" className="auth-label">
                    Password
                  </label>
                  <Link to="/reset-password" className="auth-link">
                    Forgot?
                  </Link>
                </div>
                <input
                  id="password"
                  type="password"
                  className="form-input"
                  placeholder="Your password"
                  {...register('password')}
                />
                {errors.password && (
                  <span style={{ color: '#c33', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    {errors.password.message}
                  </span>
                )}
              </div>
            </div>

            <div className="auth-form-actions">
              <button type="submit" className="btn btn-primary w-full" disabled={loading}>
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
              <div className="divider">or</div>

              <button
                type="button"
                className="btn btn-secondary w-full"
                onClick={() => handleOAuth('google')}
                disabled={loading}
              >
                Sign In with Google
              </button>
              <button
                type="button"
                className="btn btn-secondary w-full"
                onClick={() => handleOAuth('github')}
                disabled={loading}
              >
                Sign In with GitHub
              </button>
              <button
                type="button"
                className="btn btn-secondary w-full"
                onClick={() => handleOAuth('discord')}
                disabled={loading}
              >
                Sign In with Discord
              </button>
              <button
                type="button"
                className="btn btn-secondary w-full"
                onClick={() => handleOAuth('microsoft')}
                disabled={loading}
              >
                Sign In with Microsoft
              </button>
            </div>
          </form>

          <div className="auth-footer">
            Don't you have an account?{' '}
            <Link to="/signup" className="link">
              Sign Up
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
